import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useV3Window } from './useV3Window';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

// Change password: a V3 window of its own, opened by the button under the photo in My preferences. It uses the same sign-in password change as before
// (POST /auth/change-password: the current password and the new one; the server applies the password rules and says why if it refuses).
const V3ChangePassword = ({ open, onClose }) => {
  const win = useV3Window(open, 520, { single: true });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // Each time it opens the boxes are empty.
  useEffect(() => {
    if (!open) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, [open]);

  // Escape closes this window only (it is above My preferences, which must stay open).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open || !win.active) return null;

  const save = async () => {
    if (!currentPassword || !newPassword) { toast.error('Enter your current password and a new one'); return; }
    if (newPassword !== confirmPassword) { toast.error('The new password and its confirmation are not the same'); return; }
    setSaving(true);
    try {
      await api.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed');
      onClose();
    } catch (error) {
      toast.error(errorText(error, 'Could not change the password'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className={`${win.className} v3-user-prefs v3-change-password`} {...win.attrs} style={win.style} data-testid="v3-change-password">
      <div role="dialog" aria-modal="true" aria-label="Change password">
        <div className="v3-modal-bar v3-prefs-head v3-prefs-head--two">
          <h2 className="v3-prefs-title">Change password</h2>
          <button type="button" className="v3-prefs-close" onClick={onClose} aria-label="Close Change password" data-testid="v3-change-password-close">
            <X className="v3-i" />
          </button>
        </div>
        <div className="v3-pass-body">
          <input type="password" autoComplete="current-password" className="v3-prefs-input" placeholder="Current password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoFocus data-testid="v3-prefs-current-password" />
          <input type="password" autoComplete="new-password" className="v3-prefs-input" placeholder="New password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} data-testid="v3-prefs-new-password" />
          <input type="password" autoComplete="new-password" className="v3-prefs-input" placeholder="Confirm new password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') save(); }} data-testid="v3-prefs-confirm-password" />
          <div className="v3-pass-actions">
            <button type="button" className="v3-prefs-btn v3-prefs-btn--quiet" onClick={onClose} data-testid="v3-change-password-cancel">Cancel</button>
            <button type="button" className="v3-prefs-btn v3-prefs-btn--primary" disabled={saving} onClick={save} data-testid="v3-prefs-password-save">Change password</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default V3ChangePassword;
